/**
 * Safely extracts error message from unknown error type.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error !== null && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }

  return 'Unknown error';
}

/**
 * Creates a standardized error with context.
 */
export function createContextualError(
  context: string,
  originalError: unknown
): Error {
  const message = getErrorMessage(originalError);
  const contextualMessage = `${context}: ${message}`;

  const error = new Error(contextualMessage);

  if (originalError instanceof Error) {
    const { stack } = originalError;
    if (stack !== undefined) {
      error.stack = stack;
    }
    Object.assign(error, originalError);
  }

  return error;
}

export async function withErrorContext<T>(
  context: string,
  operation: () => Promise<T>
): Promise<T> {
  try {
    return await operation();
  } catch (error: unknown) {
    throw createContextualError(context, error);
  }
}
